<template>
  <el-dialog
    :visible.sync="visible"
    width="640px"
    :before-close="handleClose"
    :close-on-click-modal="true"
    custom-class="expense-modal"
    append-to-body
  >
    <!-- Header -->
    <div slot="title" class="expense-modal__header">
      <span class="expense-modal__title">{{
        editExpense ? "Editar gasto" : "Nuevo gasto"
      }}</span>
    </div>

    <div class="expense-modal__body">
      <!-- Tipo de gasto -->
      <div class="type-selector">
        <div
          :class="['type-card', { active: form.expense_type === 'overhead' }]"
          @click="setType('overhead')"
        >
          <i class="el-icon-s-home type-card__icon"></i>
          <div class="type-card__name">Gasto operativo</div>
          <div class="type-card__desc">Overhead general del negocio</div>
        </div>
        <div
          :class="['type-card', { active: form.expense_type === 'direct' }]"
          @click="setType('direct')"
        >
          <i class="el-icon-s-cooperation type-card__icon"></i>
          <div class="type-card__name">Costo directo</div>
          <div class="type-card__desc">Asignado a proyecto o factura</div>
        </div>
      </div>

      <div
        v-if="errors.expense_type"
        class="field-error"
        style="margin-top: -10px; margin-bottom: 10px;"
      >
        Selecciona el tipo de gasto
      </div>

      <!-- Asignación (solo Costo directo) -->
      <transition name="slide-fade">
        <div v-if="form.expense_type === 'direct'" class="assign-box">
          <div class="assign-box__label">Asignar a</div>
          <div class="assign-tabs">
            <span
              :class="['assign-tab', { active: assignTo === 'project' }]"
              @click="assignTo = 'project'"
              >Proyecto</span
            >
            <span
              :class="['assign-tab', { active: assignTo === 'invoice' }]"
              @click="assignTo = 'invoice'"
              >Factura específica</span
            >
          </div>
          <el-select
            v-if="assignTo === 'project'"
            v-model="form.project_id"
            placeholder="Selecciona proyecto"
            clearable
            filterable
            style="width: 100%"
            size="small"
          >
            <el-option
              v-for="p in projects"
              :key="p.id"
              :label="p.name"
              :value="p.id"
            />
          </el-select>
          <el-select
            v-else
            v-model="form.invoice_id"
            placeholder="Selecciona factura"
            clearable
            filterable
            style="width: 100%"
            size="small"
          >
            <el-option
              v-for="inv in invoices"
              :key="inv.id"
              :label="
                `#${inv.number_invoice} · ${
                  inv.client ? inv.client.name : ''
                } · $${inv.total}`
              "
              :value="inv.id"
            />
          </el-select>
        </div>
      </transition>

      <!-- Categoría -->
      <div class="section-label">Categoría</div>
      <div class="cat-grid">
        <div
          v-for="cat in categories"
          :key="cat.value"
          :class="['cat-item', { active: form.category === cat.value }]"
          @click="form.category = cat.value"
        >
          <i :class="cat.icon" class="cat-item__icon"></i>
          <span class="cat-item__label">{{ cat.label }}</span>
        </div>
      </div>
      <div v-if="errors.category" class="field-error">
        Selecciona una categoría
      </div>

      <!-- Proveedor -->
      <div class="field-group" style="margin-top: 14px;">
        <div class="field-label">
          Proveedor <span class="field-optional">(opcional)</span>
        </div>
        <div class="vendor-row">
          <el-autocomplete
            v-model="form.vendor"
            :fetch-suggestions="searchVendors"
            placeholder="Buscar en directorio o escribe un nombre"
            size="small"
            style="flex: 1"
            :trigger-on-focus="true"
            clearable
            @select="onVendorSelect"
          >
            <template slot-scope="{ item }">
              <div class="vendor-option">
                <div
                  class="vendor-option__avatar"
                  :style="{ background: item._color }"
                >
                  {{ item._initials }}
                </div>
                <div class="vendor-option__info">
                  <span class="vendor-option__name">{{ item.value }}</span>
                  <span class="vendor-option__type">{{ item._type }}</span>
                </div>
              </div>
            </template>
          </el-autocomplete>
          <el-tooltip content="Agregar proveedor" placement="top">
            <button class="vendor-add-btn" @click="openAddVendor">
              <i class="el-icon-plus"></i>
            </button>
          </el-tooltip>
        </div>
      </div>

      <!-- Add vendor inline dialog -->
      <el-dialog
        v-if="showAddVendor"
        :visible="true"
        :show-close="false"
        width="420px"
        append-to-body
        custom-class="add-vendor-dialog"
        @close="showAddVendor = false"
      >
        <div slot="title" class="avd-header">
          <span class="avd-title">Nuevo proveedor</span>
          <button class="avd-close" @click="showAddVendor = false">
            <i class="el-icon-close"></i>
          </button>
        </div>
        <div class="avd-body">
          <div class="field-group">
            <div class="field-label">
              Nombre <span style="color:#f56c6c">*</span>
            </div>
            <el-input
              v-model="newVendor.name"
              placeholder="Ej: Proveedor XYZ"
              size="small"
            />
          </div>
          <div class="field-group" style="margin-top: 12px;">
            <div class="field-label">Documento (opcional)</div>
            <el-input
              v-model="newVendor.document"
              placeholder="RUC o cédula"
              size="small"
            />
          </div>
          <div class="field-group" style="margin-top: 12px;">
            <div class="field-label">Email (opcional)</div>
            <el-input
              v-model="newVendor.email"
              placeholder="correo@ejemplo.com"
              size="small"
            />
          </div>
        </div>
        <div slot="footer" class="avd-footer">
          <button class="btn-cancel" @click="showAddVendor = false">
            Cancelar
          </button>
          <button
            class="btn-save"
            :disabled="savingVendor"
            @click="submitNewVendor"
          >
            <span v-if="savingVendor"><i class="el-icon-loading"></i></span>
            <span v-else>Agregar proveedor</span>
          </button>
        </div>
      </el-dialog>

      <!-- Campos: Descripción, Monto, Fecha -->
      <div class="fields-row">
        <div class="field-group" style="flex: 2">
          <div class="field-label">Descripción</div>
          <el-input
            v-model="form.description"
            :placeholder="
              form.expense_type === 'overhead'
                ? 'Ej: Alquiler de oficina'
                : 'Ej: Subcontrato diseño logos'
            "
            size="small"
            :class="{ 'is-error': errors.description }"
          />
          <div v-if="errors.description" class="field-error">Requerido</div>
        </div>
        <div class="field-group" style="flex: 1">
          <div class="field-label">Monto</div>
          <el-input
            v-model="form.amount"
            type="number"
            placeholder="0.00"
            size="small"
            :class="{ 'is-error': errors.amount }"
          />
          <div v-if="errors.amount" class="field-error">Requerido</div>
        </div>
        <div class="field-group" style="flex: 1">
          <div class="field-label">Fecha</div>
          <el-date-picker
            v-model="form.expense_date"
            type="date"
            placeholder="Fecha"
            format="dd/MM/yyyy"
            value-format="yyyy-MM-dd"
            size="small"
            style="width: 100%"
          />
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div slot="footer" class="expense-modal__footer">
      <button class="btn-cancel" @click="handleClose">Cancelar</button>
      <button class="btn-save" :disabled="saving" @click="submit">
        <span v-if="saving"><i class="el-icon-loading"></i> Guardando...</span>
        <span v-else>{{
          editExpense ? "Actualizar gasto" : "Guardar gasto"
        }}</span>
      </button>
    </div>
  </el-dialog>
</template>

<script>
import projectService from "../../projects/services/projectService";
import invoiceService from "../../invoices/services/invoiceService";
import businessService from "@/modules/dashboard/business/services/businessService";

export default {
  name: "ExpenseFormModal",
  props: {
    show: { type: Boolean, default: false },
    editExpense: { type: Object, default: null },
    defaultProjectId: { type: Number, default: null }
  },
  data() {
    return {
      visible: false,
      saving: false,
      assignTo: "project",
      form: {
        expense_type: "",
        project_id: null,
        invoice_id: null,
        category: "",
        description: "",
        amount: "",
        expense_date: new Date().toISOString().slice(0, 10),
        vendor: ""
      },
      errors: {},
      projects: [],
      invoices: [],
      showAddVendor: false,
      savingVendor: false,
      newVendor: { name: "", document: "", email: "" },
      categories: [
        { value: "software", label: "Software", icon: "el-icon-monitor" },
        { value: "subcontract", label: "Subcontrato", icon: "el-icon-user" },
        { value: "materials", label: "Materiales", icon: "el-icon-box" },
        { value: "hosting", label: "Hosting", icon: "el-icon-connection" },
        {
          value: "travel",
          label: "Viáticos",
          icon: "el-icon-location-outline"
        },
        {
          value: "accounting",
          label: "Contabilidad",
          icon: "el-icon-data-line"
        },
        { value: "other", label: "Otro", icon: "el-icon-plus" }
      ]
    };
  },
  watch: {
    show(val) {
      this.visible = val;
      if (val) {
        if (this.editExpense) {
          this.fillFromExpense(this.editExpense);
        } else {
          this.reset();
          // Preseleccionar proyecto si viene de ProjectDetail
          if (this.defaultProjectId) {
            this.form.expense_type = "direct";
            this.form.project_id = this.defaultProjectId;
            this.assignTo = "project";
          }
        }
        this.loadProjects();
        this.loadInvoices();
      }
    },
    visible(val) {
      if (!val) this.$emit("close");
    }
  },
  methods: {
    setType(type) {
      this.form.expense_type = type;
      if (type === "overhead") {
        this.form.project_id = null;
        this.form.invoice_id = null;
      }
    },
    handleClose() {
      this.visible = false;
    },
    validate() {
      const e = {};
      if (!this.form.expense_type) e.expense_type = true;
      if (!this.form.description) e.description = true;
      if (!this.form.amount || Number(this.form.amount) <= 0) e.amount = true;
      if (!this.form.category) e.category = true;
      this.errors = e;
      return Object.keys(e).length === 0;
    },
    fillFromExpense(e) {
      this.form = {
        expense_type: e.expense_type || "direct",
        project_id: e.project ? e.project.id : null,
        invoice_id: e.invoice ? e.invoice.id : null,
        category: e.category || "",
        description: e.description || "",
        amount: e.amount || "",
        expense_date: e.expense_date || new Date().toISOString().slice(0, 10),
        vendor: e.vendor || ""
      };
      this.assignTo = e.project ? "project" : e.invoice ? "invoice" : "project";
      this.errors = {};
    },

    async submit() {
      if (!this.validate()) return;
      this.saving = true;
      try {
        const payload = { ...this.form };
        if (this.form.expense_type === "direct") {
          if (this.assignTo === "project") payload.invoice_id = null;
          else payload.project_id = null;
        }

        let res;
        if (this.editExpense) {
          res = await projectService.updateExpense(
            this.editExpense.id,
            payload
          );
        } else {
          res = await projectService.addExpense(payload);
        }

        if (res.data.success) {
          this.$notifications.success({
            message: this.editExpense
              ? "Gasto actualizado."
              : "Gasto registrado."
          });
          this.$emit("saved", res.data.data);
          this.visible = false;
        }
      } finally {
        this.saving = false;
      }
    },
    reset() {
      this.form = {
        expense_type: "",
        project_id: null,
        invoice_id: null,
        category: "",
        description: "",
        amount: "",
        expense_date: new Date().toISOString().slice(0, 10),
        vendor: ""
      };
      this.errors = {};
      this.assignTo = "project";
    },
    async searchVendors(query, cb) {
      try {
        const params = { per_page: 15 };
        if (query && query.length >= 1) params.name = query;
        const res = await businessService.getBusiness({ params });
        const items = res.data?.clients || [];
        const avatarColors = [
          "#e8f8f4",
          "#f0eeff",
          "#e8f0ff",
          "#fff3e8",
          "#ffeef0"
        ];
        const textColors = [
          "#1d9e75",
          "#7c5cbf",
          "#3b6fd4",
          "#e07c2a",
          "#c0445a"
        ];
        cb(
          items.map((b, i) => ({
            value: b.name,
            _type:
              b.contact_type === 2
                ? "Proveedor"
                : b.contact_type === 1
                ? "Cliente"
                : "Contacto",
            _initials: b.name
              ? b.name
                  .split(" ")
                  .slice(0, 2)
                  .map(w => w[0])
                  .join("")
                  .toUpperCase()
              : "?",
            _color: avatarColors[i % avatarColors.length],
            _textColor: textColors[i % textColors.length]
          }))
        );
      } catch {
        cb([]);
      }
    },

    onVendorSelect(item) {
      this.form.vendor = item.value;
    },

    openAddVendor() {
      this.newVendor = {
        name: this.form.vendor || "",
        document: "",
        email: ""
      };
      this.showAddVendor = true;
    },

    async submitNewVendor() {
      if (!this.newVendor.name.trim()) {
        this.$notifications.error({ message: "El nombre es requerido." });
        return;
      }
      this.savingVendor = true;
      try {
        const res = await businessService.addBusiness({
          name: this.newVendor.name.trim(),
          document: this.newVendor.document || null,
          email: this.newVendor.email || null,
          contact_type: 2
        });
        if (res.data && (res.data.success || res.data.client || res.data.id)) {
          const saved = res.data.client || res.data.data || res.data;
          this.form.vendor = saved.name || this.newVendor.name.trim();
          this.$notifications.success({ message: "Proveedor agregado." });
          this.showAddVendor = false;
        }
      } catch {
        this.$notifications.error({ message: "Error al agregar proveedor." });
      } finally {
        this.savingVendor = false;
      }
    },

    async loadProjects() {
      try {
        const res = await projectService.getProjects({
          skipErrorNotification: true
        });
        if (res.data.success) this.projects = res.data.data;
      } catch (e) {
        /* silent */
      }
    },
    async loadInvoices() {
      try {
        const res = await invoiceService.getInvoices({ params: { page: 1 } });
        if (res.data && res.data.data) this.invoices = res.data.data;
      } catch (e) {
        /* silent */
      }
    }
  }
};
</script>

<style lang="scss">
/* Dropdown vendor option */
.vendor-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;

  &__avatar {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 700;
    flex-shrink: 0;
    background: #e8f8f4;
    color: #1d9e75;
  }

  &__info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 1px;
    min-width: 0;
  }

  &__name {
    font-size: 13px;
    font-weight: 500;
    color: #2d2b55;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__type {
    font-size: 10px;
    color: #9ca3af;
    line-height: 1.2;
  }
}

.add-vendor-dialog {
  border-radius: 12px !important;
  overflow: hidden;

  .el-dialog__header {
    padding: 0;
    border-bottom: 1px solid #eef0f8;
  }
  .el-dialog__body {
    padding: 0;
  }
  .el-dialog__footer {
    padding: 0;
    border-top: 1px solid #eef0f8;
  }
}

.avd-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
}

.avd-title {
  font-size: 14px;
  font-weight: 600;
  color: #2d2b55;
}

.avd-close {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: #c0c4cc;
  padding: 2px;
  border-radius: 4px;
  transition: all 0.15s;
  &:hover {
    color: #2d2b55;
    background: #f5f5f5;
  }
}

.avd-body {
  padding: 20px;
}

.avd-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
}

.expense-modal {
  border-radius: 14px !important;
  overflow: hidden;

  .el-dialog__header {
    padding: 0;
    border-bottom: 1px solid #eef0f8;
  }

  .el-dialog__body {
    padding: 0;
  }

  .el-dialog__footer {
    padding: 0;
    border-top: 1px solid #eef0f8;
  }
}
</style>

<style lang="scss" scoped>
.expense-modal__header {
  padding: 14px 20px;
}

.expense-modal__title {
  font-size: 14px;
  font-weight: 600;
  color: #2d2b55;
}

.expense-modal__body {
  padding: 20px;
}

.expense-modal__footer {
  padding: 12px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

/* ── Tipo de gasto ── */
.type-selector {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.type-card {
  flex: 1;
  padding: 11px 14px;
  border-radius: 9px;
  border: 1.5px solid #e4eaf6;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #3ecfb4;
  }

  &.active {
    border-color: #1d9e75;
    background: #e6f7f3;

    .type-card__name {
      color: #1d9e75;
    }
    .type-card__desc {
      color: #3ecfb4;
    }
  }

  &__icon {
    font-size: 20px;
    color: #9ca3af;
    margin-bottom: 6px;
    display: block;
    transition: color 0.15s;
  }

  &__name {
    font-size: 12px;
    font-weight: 600;
    color: #4b5563;
    margin-bottom: 2px;
  }

  &__desc {
    font-size: 11px;
    color: #9ca3af;
  }

  &.active &__icon {
    color: #1d9e75;
  }
}

/* ── Assign box ── */
.assign-box {
  background: #f8f9fc;
  border-radius: 9px;
  padding: 12px;
  margin-bottom: 16px;

  &__label {
    font-size: 11px;
    color: #9ca3af;
    margin-bottom: 8px;
  }
}

.assign-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
}

.assign-tab {
  font-size: 11px;
  padding: 3px 10px;
  border-radius: 20px;
  border: 1px solid #e4eaf6;
  cursor: pointer;
  color: #9ca3af;
  background: transparent;
  transition: all 0.15s;

  &.active {
    background: #e6f7f3;
    border-color: #1d9e75;
    color: #1d9e75;
    font-weight: 500;
  }
}

/* ── Categoría ── */
.section-label {
  font-size: 11px;
  color: #9ca3af;
  margin-bottom: 8px;
}

.cat-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 5px;
  margin-bottom: 6px;
}

.cat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 7px 4px;
  border-radius: 8px;
  border: 1px solid #e4eaf6;
  cursor: pointer;
  transition: all 0.12s;

  &:hover {
    border-color: #3ecfb4;
  }

  &.active {
    border-color: #1d9e75;
    background: #e6f7f3;

    .cat-item__label {
      color: #3c3489;
      font-weight: 600;
    }
  }

  &__icon {
    font-size: 18px;
    color: #6b7280;
    margin-bottom: 3px;
  }
  &__label {
    font-size: 10px;
    color: #6b7280;
  }

  &.active .cat-item__icon {
    color: #1d9e75;
  }
}

/* ── Campos ── */
.fields-row {
  display: flex;
  gap: 8px;
  margin-top: 14px;
}

.field-group {
  display: flex;
  flex-direction: column;
}

.field-label {
  font-size: 11px;
  color: #9ca3af;
  margin-bottom: 4px;
}

.field-error {
  font-size: 10px;
  color: #f56c6c;
  margin-top: 3px;
}

/* ── Buttons ── */
.btn-cancel {
  padding: 7px 18px;
  border-radius: 8px;
  border: 1px solid #e4eaf6;
  background: transparent;
  color: #606266;
  font-size: 13px;
  cursor: pointer;
  &:hover {
    background: #f5f5f5;
  }
}

.btn-save {
  padding: 7px 20px;
  border-radius: 8px;
  border: none;
  background: #3ecfb4;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  &:hover {
    background: #0e8c87;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

/* ── Vendor row ── */
.vendor-row {
  display: flex;
  gap: 6px;
  align-items: center;
}

.vendor-add-btn {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1.5px solid #e4eaf6;
  background: transparent;
  color: #9ca3af;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;

  &:hover {
    border-color: #3ecfb4;
    color: #1d9e75;
    background: #e8faf6;
  }
}

/* ── Vendor autocomplete option ── */
.field-optional {
  font-size: 10px;
  color: #c0c4cc;
  font-weight: 400;
  margin-left: 3px;
}

/* ── Transition ── */
.slide-fade-enter-active {
  transition: all 0.2s ease;
}
.slide-fade-leave-active {
  transition: all 0.15s ease;
}
.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
